// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * Wiring. Owns the state, owns nothing else.
 *
 * The preview loop is driven by `setInterval`, not `requestAnimationFrame`.
 * That is deliberate: a hidden or backgrounded tab throttles rAF to nothing, so
 * an rAF-driven preview stops dead the moment somebody switches tabs and — more
 * to the point — cannot be verified at all in an automated browser, where the
 * page is always hidden. This fleet has shipped that bug before.
 */

import './styles/main.css';

import { ABOUT, HOW_IT_WORKS, LIMITS, PRIVACY } from './copy.ts';
import { bindLogToggle, logEvent } from './eventlog.ts';
import { initGlossary } from './glossary.ts';
import { openModal } from './modal.ts';
import {
  clearError,
  el,
  escapeHtml,
  renderConsent,
  renderShell,
  setProgress,
  showError,
} from './ui.ts';
import { ParallaxRenderer } from './gl/renderer.ts';
import { evalPath, overscanFor, type PathName } from './core/camera.ts';
import {
  excursionCap,
  isDepthUsable,
  jumpMap,
  jumpRadius,
  normaliseDepth,
  percentileSorted,
  sortedSample,
  depthSanity,
  type DepthField,
  type ExcursionCap,
} from './core/depth.ts';
import {
  ASPECTS,
  artefactNames,
  bitrateCeiling,
  coverUv,
  formatBytes,
  FPS,
  frameCount,
  inferenceSize,
  outputSize,
  type AspectId,
} from './core/plan.ts';
import {
  anaglyphPng,
  bokehJpeg,
  canShare,
  depthPng,
  download,
  maxTextureEdge,
  readPhoto,
  shareFile,
} from './core/artefacts.ts';
import { RUNTIME_BYTES_OVER_THE_WIRE, TIERS } from './model/config.ts';
import type { DepthRequest, DepthResponse, RenderRequest, RenderResponse, Settings } from './types.ts';

interface Loaded {
  file: File;
  bitmap: ImageBitmap;
  field: DepthField;
  /** The untouched network output, kept for the depth map's own normalisation. */
  raw: Float32Array;
  rawWidth: number;
  rawHeight: number;
  cap: ExcursionCap;
  medianDepth: number;
}

/*
 * A tool whose entire output is motion must not start moving at somebody who
 * has asked their operating system for less of it. When the preference is set
 * the preview loads on a still frame with a visible Play control, and the
 * exported clip is unaffected — the person asked for a quieter interface, not a
 * different product.
 */
const reduceMotion =
  typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

const settings: Settings = {
  path: 'sway',
  aspect: 'source',
  strength: 1,
  focus: 0.5,
  // Three seconds, not five. It loops, so length buys nothing but wall-clock,
  // and on the tier without a graphics card the wall-clock is the whole risk.
  seconds: 3,
  holeMap: false,
};

let playing = !reduceMotion;

let depthWorker: Worker | null = null;
let renderWorker: Worker | null = null;
let modelReady = false;
let modelTier: 'webgpu' | 'wasm' = 'wasm';
let loaded: Loaded | null = null;
let renderer: ParallaxRenderer | null = null;
let previewTimer: number | null = null;
let previewPhase = 0;
let pendingFile: File | null = null;

/* ── stages ─────────────────────────────────────────────────────────────── */

type Stage = 'drop' | 'consent' | 'progress' | 'studio';

function showStage(stage: Stage): void {
  el('stage-drop').hidden = stage !== 'drop';
  el('stage-consent').hidden = stage !== 'consent';
  el('stage-progress').hidden = stage !== 'progress';
  el('stage-studio').hidden = stage !== 'studio';
  el('intro').hidden = stage !== 'drop';
  if (stage !== 'studio') stopPreview();
}

/* ── the depth worker ───────────────────────────────────────────────────── */

function ensureDepthWorker(): Worker {
  if (depthWorker) return depthWorker;
  const worker = new Worker(new URL('./workers/depth.worker.ts', import.meta.url), { type: 'module' });
  worker.onmessage = (event: MessageEvent<DepthResponse>) => handleDepthMessage(event.data);
  worker.onerror = (event) => {
    showError(`The depth model could not start: ${event.message}`, () => beginDepth());
    logEvent('error', `depth worker: ${event.message}`);
  };
  depthWorker = worker;
  return worker;
}

function postDepth(message: DepthRequest, transfer: Transferable[] = []): void {
  ensureDepthWorker().postMessage(message, transfer);
}

function handleDepthMessage(message: DepthResponse): void {
  if (message.kind === 'progress') {
    const mb = (n: number) => `${(n / 1e6).toFixed(1)} MB`;
    setProgress(message.stage, message.loaded, message.total, `${mb(message.loaded)} of ${mb(message.total)}`);
    return;
  }
  if (message.kind === 'ready') {
    modelReady = true;
    modelTier = message.device === 'webgpu' ? 'webgpu' : 'wasm';
    logEvent(
      'good',
      `Model ready on ${message.device} (${message.dtype}), ${message.threads} thread${message.threads === 1 ? '' : 's'}`,
    );
    for (const line of message.rejected) logEvent('info', line);
    if (pendingFile) void estimate(pendingFile);
    return;
  }
  if (message.kind === 'depth') {
    logEvent('good', `Depth estimated in ${message.ms} ms (${message.width}×${message.height})`);
    void onDepth(new Float32Array(message.raw), message.width, message.height, message.ms);
    return;
  }
  if (message.kind === 'error') {
    showError(message.message, () => beginDepth());
    logEvent('error', message.message);
    showStage('drop');
  }
}

/* ── opening a photograph ───────────────────────────────────────────────── */

const ACCEPTED = /^image\//;

async function openPhoto(file: File): Promise<void> {
  clearError();
  if (!ACCEPTED.test(file.type) && !/\.(jpe?g|png|webp|avif|gif|bmp)$/i.test(file.name)) {
    showError(`${file.name} does not look like a picture this browser can open.`);
    return;
  }
  logEvent('info', `Opened ${file.name} (${formatBytes(file.size)})`);
  pendingFile = file;

  if (modelReady) {
    await estimate(file);
    return;
  }

  let bitmap: ImageBitmap;
  try {
    bitmap = await readPhoto(file, maxTextureEdge());
  } catch {
    showError(`This browser could not decode ${file.name}. JPEG, PNG, WebP and AVIF all work.`);
    return;
  }
  const tier = TIERS[hasWebGpu() ? 'webgpu' : 'wasm'];
  el('stage-consent').innerHTML = renderConsent(
    { name: file.name, width: bitmap.width, height: bitmap.height, bytes: file.size },
    {
      label: tier.label,
      bytes: tier.bytes,
      runtimeBytes: RUNTIME_BYTES_OVER_THE_WIRE,
      slow: tier.device !== 'webgpu',
    },
  );
  bitmap.close();
  showStage('consent');
  el('consent-go').addEventListener('click', () => beginDepth());
  el('consent-cancel').addEventListener('click', () => {
    pendingFile = null;
    showStage('drop');
  });
}

function hasWebGpu(): boolean {
  return typeof (navigator as Navigator & { gpu?: unknown }).gpu !== 'undefined';
}

function beginDepth(): void {
  clearError();
  showStage('progress');
  setProgress('Getting the depth model', 0, 1, '', 'once only, then kept by your browser');
  const base = new URL('ort/', document.baseURI).href;
  postDepth({ kind: 'load', ortBase: base });
}

async function estimate(file: File): Promise<void> {
  showStage('progress');
  setProgress('Reading the picture', 0, 1);
  const cap = maxTextureEdge();
  const bitmap = await readPhoto(file, cap);
  logEvent('info', `Working at ${bitmap.width}×${bitmap.height} (this machine's texture limit is ${cap})`);
  const size = inferenceSize(bitmap.width, bitmap.height);
  if (size.width !== bitmap.width) {
    logEvent('info', `Measuring depth at ${size.width}×${size.height}`);
  }

  const canvas = document.createElement('canvas');
  canvas.width = size.width;
  canvas.height = size.height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('this browser has no 2D canvas');
  ctx.drawImage(bitmap, 0, 0, size.width, size.height);
  const image = ctx.getImageData(0, 0, size.width, size.height);

  loadedBitmap?.close();
  loadedBitmap = bitmap;
  loadedFile = file;

  setProgress(
    modelTier === 'webgpu' ? 'Working out the depth' : 'Working out the depth on the processor',
    0,
    1,
    '',
    modelTier === 'webgpu'
      ? ''
      : 'without a graphics card this is tens of seconds — there is no faster path on a static site',
  );
  const buffer = image.data.buffer as ArrayBuffer;
  postDepth({ kind: 'estimate', rgba: buffer, width: size.width, height: size.height }, [buffer]);
}

let loadedBitmap: ImageBitmap | null = null;
let loadedFile: File | null = null;

async function onDepth(raw: Float32Array, width: number, height: number, ms: number): Promise<void> {
  if (!loadedBitmap || !loadedFile) return;
  const { field, report } = normaliseDepth(raw, width, height);
  const sanity = depthSanity(field);
  if (!isDepthUsable(sanity)) {
    showError(
      'There is almost no depth in this picture — it reads as one flat surface. A photograph of a document or a plain wall will do that, and so will a depth model that failed to run properly on this machine.',
      () => {
        if (loadedFile) void estimate(loadedFile);
      },
    );
    logEvent('warn', `Depth field is flat (spread ${sanity.stdDev.toFixed(4)}) — refusing to animate it`);
    showStage('drop');
    return;
  }
  logEvent(
    'info',
    `Depth spread ${sanity.stdDev.toFixed(3)}, clipped ${(report.clippedLow * 100).toFixed(1)}% near / ${(report.clippedHigh * 100).toFixed(1)}% far`,
  );

  const out = outputSize(settings.aspect, loadedBitmap.width, loadedBitmap.height);
  const radius = jumpRadius(field.width, field.height);
  const jump = jumpMap(field, radius);
  const cap = excursionCap(jump, out.width);
  const median = percentileSorted(sortedSample(field.data), 0.5);
  settings.focus = median;

  logEvent(
    'good',
    `Travel limit ${(cap.fraction * 100).toFixed(2)}% of the frame (${cap.parallaxPx.toFixed(1)} px; edge p99.5 ${cap.j995.toFixed(3)}, worst ${cap.jmax.toFixed(3)})`,
  );
  logEvent('info', `Depth took ${ms} ms on ${modelTier}`);

  loaded = {
    file: loadedFile,
    bitmap: loadedBitmap,
    field,
    raw,
    rawWidth: width,
    rawHeight: height,
    cap,
    medianDepth: median,
  };
  el<HTMLInputElement>('range-focus').value = String(Math.round(median * 100));
  enterStudio(true);
}

/* ── the studio ─────────────────────────────────────────────────────────── */

/*
 * `fresh` is only true when a NEW photograph has just been measured.
 *
 * Returning to the studio after an export must NOT clear the artefacts, and it
 * used to: the clip card was added and then wiped by this function one line
 * later, so a person waited out a whole render and was handed nothing. Nothing
 * short of running an export end to end would have shown it — the card is
 * created correctly, and then deleted.
 */
function enterStudio(fresh = false): void {
  if (!loaded) return;
  showStage('studio');
  if (fresh) {
    el('artefacts').hidden = true;
    el('artefacts').replaceChildren();
  }
  rebuildRenderer();
  updateCapNote();
  startPreview();
}

function previewSize(): { width: number; height: number } {
  if (!loaded) return { width: 2, height: 2 };
  const out = outputSize(settings.aspect, loaded.bitmap.width, loaded.bitmap.height);
  const plateWidth = Math.max(240, el('plate').clientWidth || 640);
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const scale = Math.min(1, plateWidth / out.width);
  return {
    width: Math.max(2, Math.round(out.width * scale * dpr)),
    height: Math.max(2, Math.round(out.height * scale * dpr)),
  };
}

function rebuildRenderer(): void {
  if (!loaded) return;
  renderer?.dispose();
  const canvas = el<HTMLCanvasElement>('preview');
  renderer = new ParallaxRenderer(canvas);
  canvas.addEventListener('webglcontextlost', onContextLost as EventListener, { once: true });
  const size = previewSize();
  if (!renderer.resize(size.width, size.height)) return;
  const out = outputSize(settings.aspect, loaded.bitmap.width, loaded.bitmap.height);
  const cover = coverUv(loaded.bitmap.width, loaded.bitmap.height, out.width, out.height);
  renderer.setPhoto(loaded.bitmap);
  renderer.setDepth(loaded.field, cover);
}

function onContextLost(event: Event): void {
  event.preventDefault();
  stopPreview();
  logEvent('warn', 'The graphics context was lost — rebuilding it');
  showError('The graphics context was lost, usually because another program took the GPU.', () => {
    rebuildRenderer();
    startPreview();
  });
}

function effectiveAmplitude(): number {
  if (!loaded) return 0;
  // The cap is a peak-to-peak travel; the slider scales it, and past 100% the
  // hole map switches itself on because the invention is now visible by
  // construction rather than by accident.
  return loaded.cap.fraction * settings.strength;
}

function drawPreview(): void {
  if (!renderer || !loaded) return;
  const size = previewSize();
  if (!renderer.resize(size.width, size.height)) return;
  const out = outputSize(settings.aspect, loaded.bitmap.width, loaded.bitmap.height);
  const cover = coverUv(loaded.bitmap.width, loaded.bitmap.height, out.width, out.height);
  const amp = effectiveAmplitude() / 2;
  const camera = evalPath(settings.path, previewPhase, amp);
  const overscan = overscanFor(settings.path, amp, out.width / out.height);
  renderer.drawFrame({
    camera: { tx: camera.tx, ty: camera.ty, zoom: camera.zoom * overscan, dz: camera.dz },
    pivot: settings.focus,
    amplitude: effectiveAmplitude(),
    cover,
    holeMap: settings.holeMap || settings.strength > 1,
  });
  el('plate-note').hidden = !(settings.holeMap || settings.strength > 1);
}

function startPreview(): void {
  stopPreview();
  drawPreview();
  if (!playing) return;
  const frames = frameCount(settings.seconds);
  previewTimer = window.setInterval(() => {
    previewPhase = (previewPhase + 1 / frames) % 1;
    drawPreview();
  }, Math.round(1000 / FPS));
}

function setPlaying(next: boolean): void {
  playing = next;
  const button = document.getElementById('play-toggle');
  if (button) button.textContent = playing ? 'Pause' : 'Play';
  if (playing) startPreview();
  else stopPreview();
}

function stopPreview(): void {
  if (previewTimer !== null) {
    clearInterval(previewTimer);
    previewTimer = null;
  }
}

function updateCapNote(): void {
  if (!loaded) return;
  const out = outputSize(settings.aspect, loaded.bitmap.width, loaded.bitmap.height);
  const cap = excursionCap(jumpMapCache(loaded), out.width);
  loaded.cap = cap;
  const pct = cap.fraction * 100;
  const character =
    cap.j995 > 0.28
      ? 'This photograph has hard depth edges, so the camera is kept close — move further and the invention shows.'
      : cap.j995 < 0.12
        ? 'The depth here changes gently, so the camera has room to move.'
        : 'A middling amount of separation, so the camera gets a moderate amount of room.';
  el('cap-note').innerHTML = `
    <strong>Travel limit ${pct.toFixed(2)}% of the frame</strong> (${cap.parallaxPx.toFixed(0)} px at
    ${out.width}×${out.height}). ${character}
    ${
      settings.strength > 1
        ? `You are past it at ${Math.round(settings.strength * 100)}%, so the invented pixels are being shown.`
        : ''
    }
  `;
}

/*
 * The jump map is expensive enough to be worth not recomputing on every slider
 * move, and it does not depend on any setting — only on the depth field.
 */
const jumpCache = new WeakMap<DepthField, Float32Array>();

function jumpMapCache(state: Loaded): Float32Array {
  const cached = jumpCache.get(state.field);
  if (cached) return cached;
  const made = jumpMap(state.field, jumpRadius(state.field.width, state.field.height));
  jumpCache.set(state.field, made);
  return made;
}

/* ── artefacts ──────────────────────────────────────────────────────────── */

function artefactCard(title: string, note: string, media: HTMLElement, actions: HTMLElement): HTMLElement {
  const card = document.createElement('article');
  card.className = 'artefact';
  const h = document.createElement('h3');
  h.textContent = title;
  const p = document.createElement('p');
  p.textContent = note;
  card.append(h, media, p, actions);
  return card;
}

function saveRow(blob: Blob, filename: string, label: string): HTMLElement {
  const row = document.createElement('div');
  row.className = 'action-row';
  const save = document.createElement('button');
  save.className = 'btn btn-sm btn-primary';
  save.type = 'button';
  save.textContent = `Save ${label}`;
  save.addEventListener('click', () => download(blob, filename));
  row.append(save);

  const file = new File([blob], filename, { type: blob.type });
  if (canShare([file])) {
    const share = document.createElement('button');
    share.className = 'btn btn-sm';
    share.type = 'button';
    share.textContent = 'Share';
    share.addEventListener('click', () => {
      void shareFile(file, filename).catch((error: unknown) => {
        showError(error instanceof Error ? error.message : String(error));
      });
    });
    row.append(share);
  }
  return row;
}

function addArtefact(card: HTMLElement): void {
  const host = el('artefacts');
  host.hidden = false;
  host.append(card);
  card.scrollIntoView({ block: 'nearest' });
}

/*
 * Keep the screen awake while a clip renders.
 *
 * A phone that locks mid-export suspends the tab outright — not throttles it,
 * suspends it — and the render is lost. The lock is a request, not a guarantee,
 * and it is released the moment the export ends either way.
 */
interface WakeLockLike {
  release: () => Promise<void>;
}

let wakeLock: WakeLockLike | null = null;

async function requestWakeLock(): Promise<void> {
  const api = (navigator as Navigator & { wakeLock?: { request: (t: string) => Promise<WakeLockLike> } })
    .wakeLock;
  if (!api) return;
  try {
    wakeLock = await api.request('screen');
  } catch {
    // Refused, usually because the page is not visible. Not an error worth
    // showing anybody — the export still runs.
  }
}

function releaseWakeLock(): void {
  void wakeLock?.release().catch(() => undefined);
  wakeLock = null;
}

async function makeClip(): Promise<void> {
  if (!loaded) return;
  clearError();
  const out = outputSize(settings.aspect, loaded.bitmap.width, loaded.bitmap.height);
  const frames = frameCount(settings.seconds);
  showStage('progress');
  setProgress('Rendering', 0, frames, '', `${out.width}×${out.height}, ${frames} frames`);

  if (!renderWorker) {
    renderWorker = new Worker(new URL('./workers/render.worker.ts', import.meta.url), { type: 'module' });
    renderWorker.onmessage = (event: MessageEvent<RenderResponse>) => onRenderMessage(event.data);
    renderWorker.onerror = (event) => {
      showError(`The clip could not be rendered: ${event.message}`);
      logEvent('error', `render worker: ${event.message}`);
      enterStudio();
    };
  }

  /*
   * Let go of the preview's graphics context for the duration.
   *
   * Two live WebGL2 contexts, each holding a full-resolution copy of the
   * photograph, is how a phone runs out of memory during the one operation it
   * must not fail at — and a lost context does not throw, it emits garbage
   * frames. The preview is rebuilt when the export finishes.
   */
  stopPreview();
  renderer?.dispose();
  renderer = null;
  await requestWakeLock();

  // A clone, because the worker owns and closes whatever it is handed and the
  // preview still needs the original.
  const photo = await createImageBitmap(loaded.bitmap);
  const depth = loaded.field.data.slice().buffer as ArrayBuffer;
  const message: RenderRequest = {
    kind: 'clip',
    job: {
      photo,
      depth,
      depthWidth: loaded.field.width,
      depthHeight: loaded.field.height,
      width: out.width,
      height: out.height,
      frames,
      fps: FPS,
      path: settings.path,
      amplitude: effectiveAmplitude(),
      pivot: settings.focus,
      bitrate: bitrateCeiling(out.width, out.height),
    },
  };
  renderWorker.postMessage(message, [photo, depth]);
}

function onRenderMessage(message: RenderResponse): void {
  if (message.kind === 'progress') {
    setProgress(message.phase, message.done, message.total, `${message.done} of ${message.total} frames`);
    return;
  }
  if (message.kind === 'error') {
    releaseWakeLock();
    showError(message.message);
    logEvent('error', message.message);
    enterStudio();
    return;
  }
  if (message.kind === 'clip' && loaded) {
    releaseWakeLock();
    const names = artefactNames(loaded.file.name, message.container);
    const blob = new Blob([message.bytes as BlobPart], {
      type: message.container === 'mp4' ? 'video/mp4' : 'video/webm',
    });
    logEvent('good', `Encoded ${formatBytes(blob.size)} of ${message.codec.toUpperCase()} (${message.container})`);
    const video = document.createElement('video');
    video.src = URL.createObjectURL(blob);
    video.controls = true;
    video.loop = true;
    video.muted = true;
    video.playsInline = true;
    void video.play().catch(() => {
      /* autoplay refusal is not an error; the controls are right there */
    });
    addArtefact(
      artefactCard(
        'The clip',
        `${formatBytes(blob.size)} · loops seamlessly · no watermark`,
        video,
        saveRow(blob, names.clip, 'the clip'),
      ),
    );
    enterStudio();
  }
}

function imagePreview(blob: Blob): HTMLImageElement {
  const img = document.createElement('img');
  img.src = URL.createObjectURL(blob);
  img.alt = '';
  return img;
}

async function guard<T>(work: () => Promise<T>): Promise<T | null> {
  try {
    return await work();
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    showError(message);
    logEvent('error', message);
    return null;
  }
}

/* ── boot ───────────────────────────────────────────────────────────────── */

function bindSegment(id: string, attribute: string, onPick: (value: string) => void): void {
  el(id).addEventListener('click', (event) => {
    const button = (event.target as HTMLElement | null)?.closest<HTMLElement>(`[data-${attribute}]`);
    if (!button) return;
    for (const other of el(id).querySelectorAll('[aria-pressed]')) {
      other.setAttribute('aria-pressed', String(other === button));
    }
    onPick(button.dataset[attribute] ?? '');
  });
}

function boot(): void {
  renderShell();
  initGlossary();
  bindLogToggle(el('log-toggle'), el('log-count'));

  el('nav-how').addEventListener('click', () => openModal('How unflatten works', HOW_IT_WORKS));
  el('nav-privacy').addEventListener('click', () => openModal('Privacy', PRIVACY));
  el('nav-about').addEventListener('click', () => openModal('About unflatten', ABOUT));
  el('trust-badge').addEventListener('click', () => openModal('Privacy', PRIVACY));
  el('limits-link').addEventListener('click', () => openModal('What it can and cannot do', LIMITS));

  const input = el<HTMLInputElement>('file-input');
  const dropzone = el('dropzone');
  dropzone.addEventListener('click', () => input.click());
  dropzone.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      input.click();
    }
  });
  input.addEventListener('change', () => {
    const file = input.files?.[0];
    if (file) void openPhoto(file);
    input.value = '';
  });

  for (const type of ['dragenter', 'dragover'] as const) {
    dropzone.addEventListener(type, (event) => {
      event.preventDefault();
      dropzone.classList.add('is-over');
    });
  }
  for (const type of ['dragleave', 'drop'] as const) {
    dropzone.addEventListener(type, () => dropzone.classList.remove('is-over'));
  }
  dropzone.addEventListener('drop', (event) => {
    event.preventDefault();
    const file = event.dataTransfer?.files?.[0];
    if (file) void openPhoto(file);
  });

  document.addEventListener('paste', (event) => {
    const item = Array.from(event.clipboardData?.items ?? []).find((i) => i.type.startsWith('image/'));
    const file = item?.getAsFile();
    if (file) {
      event.preventDefault();
      void openPhoto(file);
    }
  });

  // The sample pills sit inside the drop zone, so their click must not also
  // open the file picker — and they go through the same ingestion path as a
  // real file, never a shortcut.
  for (const [id, name] of [
    ['sample-portrait', 'portrait.jpg'],
    ['sample-landscape', 'landscape.jpg'],
  ] as const) {
    el(id).addEventListener('click', (event) => {
      event.stopPropagation();
      void guard(async () => {
        const response = await fetch(`samples/${name}`);
        if (!response.ok) throw new Error(`the sample could not be loaded (${response.status})`);
        const blob = await response.blob();
        await openPhoto(new File([blob], name, { type: blob.type || 'image/jpeg' }));
      });
    });
  }

  bindSegment('seg-path', 'path', (value) => {
    settings.path = value as PathName;
    previewPhase = 0;
    drawPreview();
  });
  bindSegment('seg-aspect', 'aspect', (value) => {
    settings.aspect = value as AspectId;
    rebuildRenderer();
    updateCapNote();
    drawPreview();
  });

  const strength = el<HTMLInputElement>('range-strength');
  strength.addEventListener('input', () => {
    settings.strength = Number(strength.value) / 100;
    el('value-strength').textContent = `${strength.value}%`;
    updateCapNote();
    drawPreview();
  });

  const focus = el<HTMLInputElement>('range-focus');
  focus.addEventListener('input', () => {
    settings.focus = Number(focus.value) / 100;
    el('value-focus').textContent =
      settings.focus > 0.66 ? 'near' : settings.focus < 0.33 ? 'far' : 'mid';
    drawPreview();
  });

  el<HTMLInputElement>('range-seconds').value = String(settings.seconds);
  el('value-seconds').textContent = `${settings.seconds} s`;
  if (reduceMotion) {
    el('play-toggle').textContent = 'Play';
    logEvent('info', 'Your system asks for reduced motion, so the preview starts still');
  }

  const seconds = el<HTMLInputElement>('range-seconds');
  seconds.addEventListener('input', () => {
    settings.seconds = Number(seconds.value);
    el('value-seconds').textContent = `${seconds.value} s`;
    startPreview();
  });

  el('play-toggle').addEventListener('click', () => setPlaying(!playing));
  document.addEventListener('keydown', (event) => {
    if (event.key === ' ' && !el('stage-studio').hidden && event.target === document.body) {
      event.preventDefault();
      setPlaying(!playing);
    }
  });

  const holes = el<HTMLInputElement>('toggle-holes');
  holes.addEventListener('change', () => {
    settings.holeMap = holes.checked;
    drawPreview();
  });

  el('make-clip').addEventListener('click', () => void makeClip());
  el('make-depth').addEventListener('click', () => {
    void guard(async () => {
      if (!loaded) return;
      const blob = await depthPng(loaded.raw, loaded.rawWidth, loaded.rawHeight);
      const names = artefactNames(loaded.file.name, 'mp4');
      logEvent('good', `Depth map written, ${formatBytes(blob.size)}, 16-bit greyscale`);
      addArtefact(
        artefactCard(
          'The depth map',
          `${loaded.field.width}×${loaded.field.height}, 16-bit greyscale — white is near. Relative, with no real-world scale.`,
          imagePreview(blob),
          saveRow(blob, names.depth, 'the depth map'),
        ),
      );
    });
  });
  el('make-bokeh').addEventListener('click', () => {
    void guard(async () => {
      if (!loaded) return;
      const out = outputSize(settings.aspect, loaded.bitmap.width, loaded.bitmap.height);
      const blob = await bokehJpeg({
        photo: loaded.bitmap,
        field: loaded.field,
        width: out.width,
        height: out.height,
        focus: settings.focus,
        strengthPx: Math.max(6, out.width * 0.018),
      });
      const names = artefactNames(loaded.file.name, 'mp4');
      logEvent('good', `Lens blur rendered, ${formatBytes(blob.size)}`);
      addArtefact(
        artefactCard(
          'The lens blur',
          'A depth-graded defocus with the focal plane where you left it — a lens, applied to a photograph that never had one.',
          imagePreview(blob),
          saveRow(blob, names.bokeh, 'the lens blur'),
        ),
      );
    });
  });
  el('make-anaglyph').addEventListener('click', () => {
    void guard(async () => {
      if (!loaded) return;
      const out = outputSize(settings.aspect, loaded.bitmap.width, loaded.bitmap.height);
      const blob = await anaglyphPng({
        photo: loaded.bitmap,
        field: loaded.field,
        width: out.width,
        height: out.height,
        focus: settings.focus,
        amplitude: effectiveAmplitude(),
      });
      const names = artefactNames(loaded.file.name, 'mp4');
      logEvent('good', `Red/cyan still rendered, ${formatBytes(blob.size)}`);
      addArtefact(
        artefactCard(
          'The 3D still',
          'Red/cyan, using the Dubois projection rather than a plain colour split. Cardboard glasses required.',
          imagePreview(blob),
          saveRow(blob, names.anaglyph, 'the 3D still'),
        ),
      );
    });
  });
  el('pick-other').addEventListener('click', () => {
    stopPreview();
    loaded = null;
    showStage('drop');
  });
  el('progress-cancel').addEventListener('click', () => {
    releaseWakeLock();
    renderWorker?.postMessage({ kind: 'cancel' } satisfies RenderRequest);
    if (loaded) enterStudio();
    else showStage('drop');
  });

  window.addEventListener('resize', () => drawPreview());

  logEvent('info', `unflatten ready — ${ASPECTS.length} output shapes, nothing uploaded`);
  showStage('drop');

  if (import.meta.env.DEV) {
    /*
     * A handle for the automated dry run, tree-shaken out of production.
     *
     * It drives the REAL functions rather than a parallel path. `injectDepth`
     * exists because the automated browser has no route to Hugging Face, so the
     * one step that cannot be exercised there is the download — everything
     * downstream of the depth field (the normalisation, the travel limit, the
     * renderer, the encoder, all four artefacts) is identical whether the
     * numbers came from the network or from here, and this is how it gets
     * covered.
     */
    (window as unknown as Record<string, unknown>).__unflatten = {
      openPhoto,
      makeClip,
      settings,
      state: () => loaded,
      escapeHtml,
      async injectDepth(name: string, shape: 'subject' | 'ramp' = 'subject') {
        const response = await fetch(`samples/${name}`);
        const blob = await response.blob();
        const file = new File([blob], name, { type: blob.type });
        loadedBitmap?.close();
        loadedBitmap = await readPhoto(file, maxTextureEdge());
        loadedFile = file;
        const size = inferenceSize(loadedBitmap.width, loadedBitmap.height);
        const raw = new Float32Array(size.width * size.height);
        for (let y = 0; y < size.height; y += 1) {
          for (let x = 0; x < size.width; x += 1) {
            const u = x / size.width;
            const v = y / size.height;
            raw[y * size.width + x] =
              shape === 'ramp'
                ? 1 - v
                : // A centred elliptical subject standing well in front of a
                  // graded background: the shape whose travel limit should come
                  // out small.
                  (((u - 0.5) / 0.28) ** 2 + ((v - 0.55) / 0.42) ** 2 < 1 ? 3.2 : 0.4 + 0.5 * (1 - v));
          }
        }
        modelReady = true;
        await onDepth(raw, size.width, size.height, 0);
        return { width: size.width, height: size.height };
      },
    };
  }
}

boot();
